import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowRight, ArrowUpRight, LoaderCircle, Plus } from "lucide-react";
import api from "@/lib/api";
import { PRODUCT_TABS, TAB_COLORS, TAB_BADGES } from "@/lib/data";
import { useQuote } from "@/context/QuoteContext";
import { Reveal } from "@/components/Reveal";

const INITIAL = 5;

const QUOTE_PRODUCT_MAP = {
  ct: "CT Scan System",
  mri: "MRI Scanner",
  cathlab: "Cath Lab System",
  mammo: "Other",
  ultrasound: "Other",
};

export default function Products() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("all");
  const [expanded, setExpanded] = useState(null);
  const [showAll, setShowAll] = useState(false);
  const { openQuoteModal } = useQuote();

  useEffect(() => {
    api
      .get("/products")
      .then((res) => setProducts(res.data))
      .catch((e) => console.error("Failed to load products", e))
      .finally(() => setLoading(false));
  }, []);

  const filtered = tab === "all" ? products : products.filter((p) => p.tab === tab);
  const visible = showAll ? filtered : filtered.slice(0, INITIAL);

  const countFor = (id) => (id === "all" ? products.length : products.filter((p) => p.tab === id).length);

  const quoteFor = (p) => openQuoteModal(QUOTE_PRODUCT_MAP[p.tab] || p.badge || "Other");

  return (
    <section id="products" className="py-24 lg:py-32 bg-white" data-testid="products-section">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-10">
        <div className="flex flex-col lg:flex-row lg:items-start justify-between gap-6 mb-10">
          <div className="max-w-xl">
            <Reveal>
              <div className="text-[11.5px] uppercase tracking-[0.22em] text-medis-teal font-semibold mb-3">
                Our Product Portfolio
              </div>
              <h2 className="font-display font-bold text-medis-navy text-3xl sm:text-[38px] leading-[1.05] tracking-tight">
                Imaging equipment, shielding and cryogenic supply supplied end-to-end.
              </h2>
            </Reveal>
          </div>
          <div className="lg:max-w-xs lg:pt-2">
            <p className="text-medis-gray text-[14.5px] leading-relaxed">
              All systems sourced, verified and supplied with full technical documentation. Enquire for availability and
              pricing.
            </p>
            <button
              onClick={() => openQuoteModal()}
              className="mt-3 group inline-flex items-center gap-1.5 text-medis-blue text-[13px] font-semibold hover:text-medis-teal transition-colors"
              data-testid="request-product-details"
            >
              Request Product Details
              <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </button>
          </div>
        </div>

        <div className="flex flex-wrap gap-2 mb-8">
          {PRODUCT_TABS.map((t) => {
            const isActive = tab === t.id;
            return (
              <button
                key={t.id}
                onClick={() => {
                  setTab(t.id);
                  setShowAll(false);
                  setExpanded(null);
                }}
                className="px-4 py-2 rounded-full text-[13px] font-semibold transition-all duration-200 border"
                style={
                  isActive
                    ? { background: "#185FA5", color: "white", borderColor: "#185FA5" }
                    : { background: "white", color: "#4b5563", borderColor: "#e5e7eb" }
                }
                data-testid={`products-tab-${t.id}`}
              >
                {t.label}
                <span
                  className="ml-2 text-[10px] font-bold px-1.5 py-0.5 rounded-full"
                  style={
                    isActive
                      ? { background: "rgba(255,255,255,0.25)", color: "white" }
                      : { background: "#f3f4f6", color: "#6b7280" }
                  }
                >
                  {countFor(t.id)}
                </span>
              </button>
            );
          })}
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20 gap-3 text-medis-gray/50" data-testid="products-loading">
            <LoaderCircle className="h-5 w-5 animate-spin" />
            <span className="text-[14px]">Loading products...</span>
          </div>
        ) : visible.length === 0 ? (
          <div className="py-16 text-center text-medis-gray" data-testid="products-empty">
            <p className="text-[15px]">Product listings are being updated. Please enquire directly for availability.</p>
          </div>
        ) : (
          <div className="flex flex-col gap-3" data-testid="products-list">
            {visible.map((p, i) => {
              const color = TAB_COLORS[p.tab] || "#185FA5";
              const isOpen = expanded === p.id;
              return (
                <motion.div
                  key={p.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.1 }}
                  transition={{ duration: 0.5, delay: Math.min(i * 0.06, 0.3) }}
                  className="rounded-xl border border-medis-silver/30 bg-white overflow-hidden hover:shadow-lg transition-shadow duration-300"
                  data-testid={`product-card-${i}`}
                >
                  <div
                    role="button"
                    tabIndex={0}
                    onClick={() => setExpanded(isOpen ? null : p.id)}
                    onKeyDown={(e) => e.key === "Enter" && setExpanded(isOpen ? null : p.id)}
                    className="w-full flex items-center justify-between gap-4 px-5 sm:px-6 py-5 text-left cursor-pointer"
                    data-testid={`product-expand-${i}`}
                  >
                    <div className="flex items-center gap-4 min-w-0">
                      <div
                        className="hidden sm:flex h-10 w-10 rounded-lg items-center justify-center flex-shrink-0 text-[11px] font-bold"
                        style={{ background: `${color}14`, color }}
                      >
                        {String(i + 1).padStart(2, "0")}
                      </div>
                      <div className="min-w-0">
                        <div className="text-[10.5px] uppercase tracking-[0.16em] font-bold mb-1" style={{ color }}>
                          {p.badge}
                        </div>
                        <div className="font-display font-semibold text-medis-navy text-[16px] sm:text-[18px] truncate">
                          {p.name}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 sm:gap-3 flex-shrink-0">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          quoteFor(p);
                        }}
                        className="hidden sm:inline-flex items-center gap-1.5 rounded-md border border-medis-blue/25 text-medis-blue px-3.5 py-2 text-[12px] font-semibold hover:bg-medis-blue hover:text-white transition-colors"
                        data-testid={`product-quote-${i}`}
                      >
                        Get a Quote
                        <ArrowRight className="h-3.5 w-3.5" />
                      </button>
                      <span
                        className={`text-[10.5px] font-semibold px-2.5 py-1 rounded-full ${
                          p.status === "In Stock" ? "bg-emerald-50 text-emerald-600" : "bg-amber-50 text-amber-600"
                        }`}
                      >
                        {p.status}
                      </span>
                      <span
                        className="flex-shrink-0 h-7 w-7 rounded-full flex items-center justify-center border transition-all duration-300"
                        style={isOpen ? { background: color, borderColor: color, color: "white" } : { borderColor: "#d1d5db", color: "#6b7280" }}
                      >
                        <Plus className={`h-3.5 w-3.5 transition-transform duration-300 ${isOpen ? "rotate-45" : ""}`} />
                      </span>
                    </div>
                  </div>

                  <div
                    style={{
                      display: "grid",
                      gridTemplateRows: isOpen ? "1fr" : "0fr",
                      transition: "grid-template-rows 0.35s cubic-bezier(0.4,0,0.2,1)",
                    }}
                  >
                    <div style={{ overflow: "hidden" }}>
                      <div className="px-5 pb-6 border-t border-medis-silver/20 pt-5">
                        <div className="flex flex-col sm:flex-row gap-5 items-start">
                          <div
                            className="flex-shrink-0 w-full h-40 sm:w-96 sm:h-64 rounded-xl overflow-hidden border bg-medis-subtle"
                            style={{ borderColor: `${color}20` }}
                          >
                            {p.image ? (
                              <img src={p.image} alt={p.name} className="w-full h-full object-cover object-center" />
                            ) : (
                              <div className="w-full h-full flex flex-col items-center justify-center gap-2 text-center p-3" style={{ background: `${color}06` }}>
                                <span className="text-[11px] font-semibold" style={{ color }}>
                                  {p.badge}
                                </span>
                                <span className="text-[9.5px] text-medis-gray/60 leading-tight">Image on request</span>
                              </div>
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-4 gap-y-3">
                              {Object.entries(p.details || {}).map(([k, v]) => (
                                <div key={k}>
                                  <div className="text-[9.5px] uppercase tracking-[0.16em] font-bold mb-1" style={{ color: `${color}90` }}>
                                    {k}
                                  </div>
                                  <div className="text-[13px] font-semibold text-medis-navy leading-snug">{v}</div>
                                </div>
                              ))}
                              <div>
                                <div className="text-[9.5px] uppercase tracking-[0.16em] font-bold mb-1" style={{ color: `${color}90` }}>
                                  Condition
                                </div>
                                <div className="text-[13px] font-semibold text-medis-navy">Refurbished</div>
                              </div>
                              <div>
                                <div className="text-[9.5px] uppercase tracking-[0.16em] font-bold mb-1" style={{ color: `${color}90` }}>
                                  Availability
                                </div>
                                <div className={`text-[13px] font-semibold ${p.status === "In Stock" ? "text-emerald-600" : "text-amber-600"}`}>
                                  {p.status}
                                </div>
                              </div>
                            </div>
                            <button
                              onClick={() => quoteFor(p)}
                              className="mt-5 inline-flex items-center gap-2 rounded-md bg-medis-blue text-white px-5 py-2.5 text-[13px] font-semibold hover:bg-medis-blue-dark transition-colors"
                              data-testid={`product-enquire-${i}`}
                            >
                              Get a Quote
                              <ArrowRight className="h-4 w-4" />
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        )}

        {filtered.length > INITIAL && (
          <div className="mt-8 flex justify-center">
            <button
              onClick={() => setShowAll((s) => !s)}
              className="px-6 py-3 rounded-full bg-medis-blue text-white text-[14px] font-semibold hover:bg-medis-blue-dark transition-colors"
              data-testid="products-show-more"
            >
              {showAll ? "Show Less" : `View More (${filtered.length - INITIAL} more)`}
            </button>
          </div>
        )}
      </div>
    </section>
  );
}
